import { useEffect, useRef, useState } from "react";
import { ArrowDownWideNarrow, ArrowLeft, ArrowUp, ArrowUpAZ, Building2, ChevronDown, ChevronRight, Folder, FolderPlus, Layers, Pencil, RefreshCw, LayoutGrid, Link2, List, FileText, Loader2, MoreHorizontal, Pause, Pin, Plus, Search, Sparkles, StickyNote, Trash2, Upload, Users, Workflow, type LucideIcon } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import AdminTabActionBar from "../AdminTabActionBar";
import KnowledgeItemEditor from "./KnowledgeItemEditor";
import AIInstructionsSettings from "./AIInstructionsSettings";
import KnowledgeScopeLabel from "./KnowledgeScope";
import { createKnowledgeItem, filterKnowledgeItems, isBusinessProfile, knowledgeScope, scopeOrder, type KnowledgeItem, type KnowledgeScope, type KnowledgeType } from "./model";
import { knowledgeRequest, knowledgeFileUrl, useKnowledgeLibrarySources } from "./useKnowledgeLibrarySources";
import GeneralKnowledge from "./GeneralKnowledge";

type DisplayKnowledgeType = "file" | "note" | "url";
type KnowledgeSection = "general" | "instructions";
interface KnowledgeFolder {
  id: string;
  name: string;
  createdAt: string;
}
interface KnowledgeFolderState {
  folders: KnowledgeFolder[];
  itemFolderIds: Record<string, string>;
  favouriteFolderIds: string[];
}
const displayTypeConfig: Record<DisplayKnowledgeType, { Icon: LucideIcon; label: string; color: string }> = {
  file: { Icon: FileText, label: "File", color: "text-muted-foreground" },
  note: { Icon: StickyNote, label: "Note", color: "text-yellow-500 dark:text-yellow-400" },
  url: { Icon: Link2, label: "Link", color: "text-violet-600 dark:text-violet-400" },
};
const SHOW_TEST_AI = false;
const checkboxStyle = "border-border data-[state=checked]:border-foreground data-[state=checked]:bg-foreground data-[state=checked]:text-background data-[state=indeterminate]:border-foreground data-[state=indeterminate]:bg-foreground data-[state=indeterminate]:text-background focus-visible:ring-muted-foreground";
const descriptions: Record<KnowledgeType, string> = { file: "PDF, DOCX, TXT, Markdown", note: "Write or paste information", faq: "Instructions and reference content", website: "Add a website to import", instructions: "Set your AI’s voice", offer: "Instructions and reference content" };
const starterInstructions: KnowledgeItem = { id: "instructions", name: "AI instructions", type: "instructions", status: "Draft", content: "", detail: descriptions.instructions };
const dateLabel = (value?: string) => value && !Number.isNaN(Date.parse(value)) ? new Date(value).toLocaleDateString("en-SG", { day: "numeric", month: "short", year: "numeric" }) : "—";
const emptyFolderState = (): KnowledgeFolderState => ({ folders: [], itemFolderIds: {}, favouriteFolderIds: [] });

function readFolderState(key: string): KnowledgeFolderState {
  if (typeof window === "undefined") return emptyFolderState();
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(key) ?? "null");
    if (!parsed || typeof parsed !== "object") return emptyFolderState();
    const candidate = parsed as Partial<KnowledgeFolderState>;
    const folders = Array.isArray(candidate.folders)
      ? candidate.folders.filter((folder): folder is KnowledgeFolder => !!folder && typeof folder.id === "string" && typeof folder.name === "string" && typeof folder.createdAt === "string")
      : [];
    const itemFolderIds = candidate.itemFolderIds && typeof candidate.itemFolderIds === "object"
      ? Object.fromEntries(Object.entries(candidate.itemFolderIds).filter((entry): entry is [string, string] => typeof entry[1] === "string"))
      : {};
    const favouriteFolderIds = Array.isArray(candidate.favouriteFolderIds) ? candidate.favouriteFolderIds.filter((id): id is string => typeof id === "string") : [];
    return { folders, itemFolderIds, favouriteFolderIds };
  } catch {
    return emptyFolderState();
  }
}

function displayTypeFor(item: KnowledgeItem): DisplayKnowledgeType {
  if (item.type === "file") return "file";
  if (item.type === "website") return "url";
  return "note";
}

function Status({ item }: { item: KnowledgeItem }) {
  return <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs text-muted-foreground">
    <span className={`h-1.5 w-1.5 rounded-full ${item.status === "Ready" ? "bg-emerald-500" : item.status === "Needs attention" ? "bg-amber-500" : item.status === "Processing" ? "animate-pulse bg-blue-500" : "bg-muted-foreground/50"}`} />
    {item.status}
  </span>;
}

export default function KnowledgeLibrary() {
  const { businessId } = useAuth();
  // Reset transient UI state when the active business changes.
  return <Library key={businessId ?? "no-business"} businessId={businessId} />;
}

function Library({ businessId }: { businessId: string | null }) {
  const [section, setSection] = useState<KnowledgeSection>("general");
  const [processing, setProcessing] = useState(false);
  const sources = useKnowledgeLibrarySources(businessId, processing);
  const loading = sources.isLoading;
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("updated");
  const [scopeFilter, setScopeFilter] = useState<KnowledgeScope | "all">("all");
  const [view, setView] = useState<"table" | "grid">("table");
  const [selected, setSelected] = useState<string[]>([]);
  const [editing, setEditing] = useState<KnowledgeItem | null>(null);
  const [deleting, setDeleting] = useState<string[]>([]);
  const [testOpen, setTestOpen] = useState(false);
  const [focusId, setFocusId] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [staging, setStaging] = useState(false);
  const [favouritesCollapsed, setFavouritesCollapsed] = useState(false);
  const favouritesStorageKey = `dibs:knowledge-favourites:${businessId ?? "guest"}`;
  const foldersStorageKey = `dibs:knowledge-folders:${businessId ?? "guest"}`;
  const [favouriteIds, setFavouriteIds] = useState<string[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const stored = window.localStorage.getItem(favouritesStorageKey);
      const parsed: unknown = stored ? JSON.parse(stored) : [];
      return Array.isArray(parsed) && parsed.every(id => typeof id === "string") ? parsed : [];
    } catch {
      return [];
    }
  });
  const [folderState, setFolderState] = useState<KnowledgeFolderState>(() => readFolderState(foldersStorageKey));
  const [activeFolderId, setActiveFolderId] = useState<string | null>(null);
  const [folderDialog, setFolderDialog] = useState<{ mode: "create" | "rename"; folderId?: string } | null>(null);
  const [folderName, setFolderName] = useState("");
  const [deletingFolderId, setDeletingFolderId] = useState<string | null>(null);
  const [dropTargetFolderId, setDropTargetFolderId] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const businessItems = sources.data ? (sources.data.some(item => item.type === "instructions") ? sources.data : [starterInstructions, ...sources.data]) : [];
  const items = businessItems;
  const folderIds = new Set(folderState.folders.map(folder => folder.id));
  const itemFolderId = (itemId: string) => {
    const folderId = folderState.itemFolderIds[itemId];
    return folderId && folderIds.has(folderId) ? folderId : null;
  };
  const showingInstructions = section === "instructions";
  // FAQs and offers live here too; the "prospect reply scripts" FAQ is internal and stays hidden.
  const dataItems = items.filter(item => ["file", "note", "website", "offer"].includes(item.type) || (item.type === "faq" && item.name.trim().toLocaleLowerCase() !== "prospect reply scripts"));
  // Only offer scopes that have data, so the filter never leads to an empty list.
  const availableScopes = scopeOrder.filter(scope => scope === "organisation" || scope === "customer" || dataItems.some(item => knowledgeScope(item) === scope));
  const visible = filterKnowledgeItems(dataItems, query, "all", "all")
    .filter(item => itemFolderId(item.id) === activeFolderId && (scopeFilter === "all" || knowledgeScope(item) === scopeFilter))
    .sort((a, b) => {
      const byScope = scopeOrder.indexOf(knowledgeScope(a)) - scopeOrder.indexOf(knowledgeScope(b));
      if (byScope) return byScope;
      return sort === "name" ? a.name.localeCompare(b.name) : (Date.parse(b.updatedAt ?? "") || 0) - (Date.parse(a.updatedAt ?? "") || 0);
    });
  const profileItems = activeFolderId === null ? visible.filter(isBusinessProfile) : [];
  const otherItems = activeFolderId === null ? visible.filter(item => !isBusinessProfile(item)) : visible;
  const selectable = visible;
  const selectedVisible = selectable.filter(item => selected.includes(item.id));
  const selectedItems = selected.map(id => dataItems.find(item => item.id === id)).filter((item): item is KnowledgeItem => !!item);
  const canDeleteSelected = selectedItems.length === selected.length && selectedItems.every(item => !item.associatedContact);
  const favouriteItems = favouriteIds.map(id => dataItems.find(item => item.id === id)).filter((item): item is KnowledgeItem => !!item);
  const favouriteFolders = showingInstructions ? [] : folderState.favouriteFolderIds.map(id => folderState.folders.find(folder => folder.id === id)).filter((folder): folder is KnowledgeFolder => !!folder);
  const activeFolder = folderState.folders.find(folder => folder.id === activeFolderId) ?? null;
  const visibleFolders = !showingInstructions && activeFolderId === null
    ? folderState.folders.filter(folder => folder.name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase())).sort((a, b) => a.name.localeCompare(b.name))
    : [];

  const changeSection = (nextSection: KnowledgeSection) => {
    setSection(nextSection);
    setActiveFolderId(null);
    setQuery("");
    setSelected([]);
  };

  useEffect(() => {
    try {
      window.localStorage.setItem(favouritesStorageKey, JSON.stringify(favouriteIds));
    } catch {
      // Favourites remain available for this session when storage is blocked.
    }
  }, [favouriteIds, favouritesStorageKey]);

  useEffect(() => {
    try {
      window.localStorage.setItem(foldersStorageKey, JSON.stringify(folderState));
    } catch {
      // Folder organisation remains available for this session when storage is blocked.
    }
  }, [folderState, foldersStorageKey]);

  const toggleFavourite = (item: KnowledgeItem) => {
    setFavouriteIds(current => current.includes(item.id) ? current.filter(id => id !== item.id) : [...current, item.id]);
  };

  const openFolderDialog = (mode: "create" | "rename", folder?: KnowledgeFolder) => {
    setFolderName(folder?.name ?? "");
    setFolderDialog({ mode, folderId: folder?.id });
  };
  const saveFolder = () => {
    const name = folderName.trim();
    if (!name) return toast.error("Give this folder a name.");
    if (name.length > 80) return toast.error("Folder names can be up to 80 characters.");
    const duplicate = folderState.folders.some(folder => folder.id !== folderDialog?.folderId && folder.name.toLocaleLowerCase() === name.toLocaleLowerCase());
    if (duplicate) return toast.error("A folder with this name already exists.");
    const createdFolder = folderDialog?.mode === "create" ? { id: crypto.randomUUID(), name, createdAt: new Date().toISOString() } : null;
    setFolderState(current => folderDialog?.mode === "rename"
      ? { ...current, folders: current.folders.map(folder => folder.id === folderDialog.folderId ? { ...folder, name } : folder) }
      : { ...current, folders: [...current.folders, createdFolder!] });
    if (createdFolder) {
      setActiveFolderId(null);
      setQuery("");
      setSelected([]);
    }
    toast.success(folderDialog?.mode === "rename" ? "Folder renamed" : "Folder created");
    setFolderDialog(null);
    setFolderName("");
  };
  const deleteFolder = (folderId: string) => {
    setFolderState(current => ({
      folders: current.folders.filter(folder => folder.id !== folderId),
      itemFolderIds: Object.fromEntries(Object.entries(current.itemFolderIds).filter(([, id]) => id !== folderId)),
      favouriteFolderIds: current.favouriteFolderIds.filter(id => id !== folderId),
    }));
    if (activeFolderId === folderId) setActiveFolderId(null);
    setDeletingFolderId(null);
    toast.success("Folder deleted. Its items were moved to All knowledge.");
  };
  const toggleFolderFavourite = (folderId: string) => {
    setFolderState(current => ({
      ...current,
      favouriteFolderIds: current.favouriteFolderIds.includes(folderId)
        ? current.favouriteFolderIds.filter(id => id !== folderId)
        : [...current.favouriteFolderIds, folderId],
    }));
  };
  const moveItems = (itemIds: string[], folderId: string | null) => {
    if (!itemIds.length) return;
    setFolderState(current => {
      const itemFolderIds = { ...current.itemFolderIds };
      itemIds.forEach(id => folderId ? itemFolderIds[id] = folderId : delete itemFolderIds[id]);
      return { ...current, itemFolderIds };
    });
    setSelected([]);
    toast.success(folderId ? `Moved ${itemIds.length === 1 ? "item" : `${itemIds.length} items`} to ${folderState.folders.find(folder => folder.id === folderId)?.name ?? "folder"}` : `Moved ${itemIds.length === 1 ? "item" : `${itemIds.length} items`} to All knowledge`);
  };
  const startItemDrag = (event: React.DragEvent, itemId: string) => {
    const ids = selected.includes(itemId) ? selected : [itemId];
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("application/x-dibs-knowledge-items", JSON.stringify(ids));
  };
  const dropItems = (event: React.DragEvent, folderId: string | null) => {
    const payload = event.dataTransfer.getData("application/x-dibs-knowledge-items");
    if (!payload) return;
    event.preventDefault();
    event.stopPropagation();
    setDropTargetFolderId(null);
    try {
      const ids: unknown = JSON.parse(payload);
      if (Array.isArray(ids) && ids.every(id => typeof id === "string")) moveItems(ids, folderId);
    } catch {
      toast.error("Could not move these items.");
    }
  };
  const openFile = async (item: KnowledgeItem) => {
    if (!businessId) return;
    // Open synchronously so the browser treats this as a user-initiated action.
    const fileWindow = window.open("", "_blank");
    if (!fileWindow) {
      toast.error("Allow pop-ups to open this file, then try again.");
      return;
    }
    fileWindow.opener = null;
    try {
      const url = await knowledgeFileUrl(businessId, item);
      if (!fileWindow.closed) fileWindow.location.href = url;
    } catch (error) {
      fileWindow.close();
      toast.error(error instanceof Error ? error.message : "Could not open file");
    }
  };
  const openItem = (item: KnowledgeItem) => {
    if (item.associatedContact || item.type === "file" || item.status !== "Processing") setEditing(item);
  };

  const processItem = async (id: string) => {
    if (!businessId) return;
    setProcessing(true);
    try {
      const result = await knowledgeRequest(businessId, "process", { id });
      if (result.processingError) toast.error(result.processingError);
      else toast.success("Content is ready for your AI");
    } finally { setProcessing(false); await sources.refetch(); }
  };
  const saveItem = async (item: KnowledgeItem): Promise<KnowledgeItem | null> => {
    if (!businessId || saving || item.associatedContact) return null;
    let saved: KnowledgeItem | null = null;
    const isNew = !items.some(existing => existing.id === item.id);
    setSaving(true);
    try {
      const result = await knowledgeRequest(businessId, "save", { item });
      if (isNew && activeFolderId && result.item) {
        setFolderState(current => ({ ...current, itemFolderIds: { ...current.itemFolderIds, [result.item!.id]: activeFolderId } }));
      }
      setEditing(null);
      await sources.refetch();
      toast.success("Knowledge saved");
      saved = result.item ?? item;
      if (result.needsProcessing && result.item) await processItem(result.item.id);
    } catch (error) { toast.error((error as Error).message); }
    finally { setSaving(false); await sources.refetch(); }
    return saved;
  };
  // Categories chosen before they were saved with the entries (browser only).
  const saveLegacyCategories = async (categories: Record<string, string>) => {
    if (!businessId) return false;
    try {
      await knowledgeRequest(businessId, "set_categories", { categories });
      await sources.refetch();
      return true;
    } catch {
      return false;
    }
  };
  const deleteOne = async (item: KnowledgeItem): Promise<boolean> => {
    if (!businessId || busy || item.associatedContact) return false;
    setBusy(true);
    try {
      const result = await knowledgeRequest(businessId, "delete", { ids: [item.id] });
      if (result.cleanupWarning) toast.warning("Knowledge removed. Some stored files could not be cleaned up.");
      else toast.success("Knowledge deleted");
      setFavouriteIds(current => current.filter(id => id !== item.id));
      return true;
    } catch (error) { toast.error((error as Error).message); return false; }
    finally { setBusy(false); await sources.refetch(); }
  };
  const saveSettingsItem = async (item: KnowledgeItem): Promise<KnowledgeItem> => {
    if (!businessId) throw new Error("A business is required.");
    const result = await knowledgeRequest(businessId, "save", { item });
    if (!result.item) throw new Error("The setting could not be saved.");
    await sources.refetch();
    return result.item;
  };
  const toggleSettingsItem = async (item: KnowledgeItem, enabled: boolean): Promise<KnowledgeItem> => {
    if (!businessId) throw new Error("A business is required.");
    const result = await knowledgeRequest(businessId, "set_enabled", { id: item.id, enabled, updatedAt: item.updatedAt });
    if (!result.item) throw new Error("The offer could not be updated.");
    await sources.refetch();
    return result.item;
  };
  const toggleItem = async (item: KnowledgeItem) => {
    if (!businessId || busy || item.associatedContact) return;
    setBusy(true);
    try {
      await knowledgeRequest(businessId, "set_enabled", { id: item.id, enabled: item.status === "Paused", updatedAt: item.updatedAt });
      toast.success(item.status === "Paused" ? "Knowledge resumed" : "Knowledge paused");
    } catch (error) { toast.error((error as Error).message); }
    finally { setBusy(false); await sources.refetch(); }
  };
  const deleteItems = async () => {
    if (!businessId || busy) return;
    setBusy(true);
    try {
      for (let i = 0; i < deleting.length; i += 100) {
        const ids = deleting.slice(i, i + 100);
        const result = await knowledgeRequest(businessId, "delete", { ids });
        setSelected(current => current.filter(id => !ids.includes(id)));
        setDeleting(current => current.filter(id => !ids.includes(id)));
        setFavouriteIds(current => current.filter(id => !ids.includes(id)));
        setFolderState(current => ({ ...current, itemFolderIds: Object.fromEntries(Object.entries(current.itemFolderIds).filter(([id]) => !ids.includes(id))) }));
        if (result.cleanupWarning) toast.warning("Knowledge removed. Some stored files could not be cleaned up.");
      }
      toast.success("Knowledge deleted");
    } catch (error) { toast.error((error as Error).message); }
    finally { setBusy(false); await sources.refetch(); }
  };
  const create = (kind: KnowledgeType) => {
    if (kind === "file") input.current?.click();
    else setEditing(createKnowledgeItem(kind));
  };
  const uploadFiles = async (files: File[]) => {
    if (staging || !businessId) return;
    setStaging(true);
    try {
      for (const file of files) {
        if (!/\.(pdf|docx|txt|md)$/i.test(file.name) || file.size === 0 || file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name}: choose a non-empty PDF, DOCX, TXT, or Markdown file up to 10 MB.`);
          continue;
        }
        try {
          const prepared = await knowledgeRequest(businessId, "prepare_upload", { name: file.name, size: file.size });
          const { error } = await supabase.storage.from("knowledge-files").uploadToSignedUrl(prepared.path!, prepared.token!, file);
          if (error) throw error;
          if (activeFolderId && prepared.item) {
            setFolderState(current => ({ ...current, itemFolderIds: { ...current.itemFolderIds, [prepared.item!.id]: activeFolderId } }));
          }
          setQuery("");
          await processItem(prepared.item!.id);
        } catch (error) { toast.error(`${file.name}: ${(error as Error).message}`); }
      }
    } finally { setStaging(false); await sources.refetch(); }
  };
  const addMenu = <DropdownMenu>
    <DropdownMenuTrigger asChild><Button size="sm" disabled={staging || saving || !businessId} className="h-7 gap-1.5 px-2.5">{staging ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}{staging ? "Uploading…" : "Add data"}</Button></DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-64">
      <DropdownMenuItem onSelect={() => openFolderDialog("create")} className="items-start gap-3 py-2.5"><FolderPlus className="mt-0.5 h-4 w-4 text-sky-600" /><div><p>Create folder</p><p className="mt-0.5 text-xs text-muted-foreground">Organise knowledge items</p></div></DropdownMenuItem>
      <DropdownMenuSeparator />
      {([
        ["file", "Upload files", "file"],
        ["note", "Write a note", "note"],
        ["website", "Import website", "url"],
      ] as const).map(([kind, label, displayType]) => { const { Icon, color } = displayTypeConfig[displayType]; return <DropdownMenuItem key={kind} onSelect={() => create(kind)} className="items-start gap-3 py-2.5">
        <Icon className={`mt-0.5 h-4 w-4 ${color}`} /><div><p>{label}</p><p className="mt-0.5 text-xs text-muted-foreground">{descriptions[kind]}</p></div>
      </DropdownMenuItem>; })}
    </DropdownMenuContent>
  </DropdownMenu>;

  const itemActions = (item: KnowledgeItem) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8" aria-label={`Actions for ${item.name}`} disabled={busy}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => toggleFavourite(item)}><Pin className={`mr-2 h-3.5 w-3.5 ${favouriteIds.includes(item.id) ? "fill-current" : ""}`} />{favouriteIds.includes(item.id) ? "Remove from favourites" : "Add to favourites"}</DropdownMenuItem>
        {itemFolderId(item.id) && <DropdownMenuItem onSelect={() => moveItems([item.id], null)}><ArrowLeft className="mr-2 h-3.5 w-3.5" />Move to All knowledge</DropdownMenuItem>}
        <DropdownMenuSeparator />
        {item.type === "file" && item.hasOriginalFile !== false && !item.isMarkdownFile && <DropdownMenuItem onSelect={() => void openFile(item)}><FileText className="mr-2 h-3.5 w-3.5" />Open file</DropdownMenuItem>}
        <DropdownMenuItem disabled={item.status === "Processing" && !item.associatedContact} onSelect={() => setEditing(item)}>Open details</DropdownMenuItem>
        {item.canProcess && <DropdownMenuItem onSelect={() => { setBusy(true); void processItem(item.id).catch(error => toast.error(error.message)).finally(() => setBusy(false)); }}><RefreshCw className="mr-2 h-3.5 w-3.5" />{item.status === "Ready" ? "Refresh content" : "Retry processing"}</DropdownMenuItem>}
        {item.type !== "instructions" && !item.associatedContact && <>
          <DropdownMenuItem onSelect={() => void toggleItem(item)}><Pause className="mr-2 h-3.5 w-3.5" />{item.status === "Paused" ? "Resume" : "Pause"}</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="text-destructive" onSelect={() => setDeleting([item.id])}><Trash2 className="mr-2 h-3.5 w-3.5" />Delete</DropdownMenuItem>
        </>}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const folderActions = (folder: KnowledgeFolder) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8" aria-label={`Actions for ${folder.name}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => toggleFolderFavourite(folder.id)}><Pin className={`mr-2 h-3.5 w-3.5 ${folderState.favouriteFolderIds.includes(folder.id) ? "fill-current" : ""}`} />{folderState.favouriteFolderIds.includes(folder.id) ? "Remove from favourites" : "Add to favourites"}</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => openFolderDialog("rename", folder)}><Pencil className="mr-2 h-3.5 w-3.5" />Rename</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="text-destructive" onSelect={() => setDeletingFolderId(folder.id)}><Trash2 className="mr-2 h-3.5 w-3.5" />Delete folder</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const itemRow = (item: KnowledgeItem) => { const { Icon, color, label } = displayTypeConfig[displayTypeFor(item)]; return <TableRow key={item.id} draggable onDragStart={event => startItemDrag(event, item.id)} className={`cursor-grab border-0 active:cursor-grabbing [&>td]:border-b [&>td]:border-border ${selected.includes(item.id) ? "bg-muted/50" : ""}`}>
                <TableCell className="px-3 py-1.5"><Checkbox className={checkboxStyle} aria-label={`Select ${item.name}`} checked={selected.includes(item.id)} onCheckedChange={checked => setSelected(current => checked ? [...new Set([...current, item.id])] : current.filter(id => id !== item.id))} /></TableCell>
                <TableCell className="px-3 py-1.5"><button className="flex w-full items-center gap-2.5 rounded-sm text-left outline-none focus-visible:ring-2 focus-visible:ring-ring" onClick={() => openItem(item)}>
                  <Icon className={`h-[18px] w-[18px] shrink-0 ${color}`} />
                  <span className="min-w-0 truncate text-sm font-medium">{item.name}</span>
                </button></TableCell>
                <TableCell className="px-3 py-1.5"><KnowledgeScopeLabel item={item} /></TableCell>
                <TableCell className="px-3 py-1.5 text-xs text-muted-foreground">{label}</TableCell><TableCell className="px-3 py-1.5"><Status item={item} /></TableCell><TableCell className="px-3 py-1.5 text-xs text-muted-foreground">{dateLabel(item.updatedAt)}</TableCell>
                <TableCell className="px-2 py-1.5">{itemActions(item)}</TableCell>
              </TableRow>; };

  return <div className="flex h-full min-h-0 w-full flex-col animate-in fade-in duration-300">
    <AdminTabActionBar className="admin-secondary-bar-tabs">
      <div className="admin-underline-tabs" role="tablist" aria-label="Knowledge sections">
        {([["general", "Business context"], ["instructions", "AI settings"]] as const).map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={section === value}
            data-state={section === value ? "active" : "inactive"}
            className="admin-underline-tab outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            onClick={() => changeSection(value)}
          >
            {label}
          </button>
        ))}
      </div>
    </AdminTabActionBar>
    {showingInstructions ? (sources.isLoading ? (
      <div className="flex min-h-0 flex-1 items-center justify-center py-20"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-label="Loading AI instructions" /></div>
    ) : <>
      <AIInstructionsSettings
        items={businessItems}
        loading={false}
        onSave={saveSettingsItem}
      />
    </>) : <>
      <GeneralKnowledge
        key={section}
        variant="general"
        businessId={businessId}
        items={dataItems.filter(item => !item.associatedContact)}
        loading={sources.isLoading}
        loadFailed={sources.isError}
        onRetry={() => void sources.refetch()}
        saving={saving}
        busy={busy}
        uploading={staging}
        requestedId={focusId}
        onRequestHandled={() => setFocusId(null)}
        onSave={saveItem}
        onSetEnabled={toggleSettingsItem}
        onDelete={deleteOne}
        onOpenFile={item => void openFile(item)}
        onRefresh={async item => { try { await processItem(item.id); } catch (error) { toast.error((error as Error).message); } }}
        onUpload={files => void uploadFiles(files)}
        onTest={SHOW_TEST_AI ? () => setTestOpen(true) : undefined}
        onSetCategories={saveLegacyCategories}
      />
    </>}
    <input ref={input} type="file" multiple accept=".pdf,.docx,.txt,.md" className="hidden" aria-label="Upload knowledge files" onChange={e => { void uploadFiles(Array.from(e.target.files ?? [])); e.target.value = ""; }} />
    <Sheet open={!!editing} onOpenChange={open => !open && !saving && setEditing(null)}><SheetContent className="w-full sm:max-w-[600px]">{editing && <KnowledgeItemEditor key={editing.id} item={editing} onSave={saveItem} onOpenFile={item => void openFile(item)} saving={saving} onCancel={() => setEditing(null)} />}</SheetContent></Sheet>
    <Sheet open={testOpen} onOpenChange={setTestOpen}><SheetContent className="w-full sm:max-w-[520px]"><TestPanel businessId={businessId} items={items} onOpenItem={item => { setTestOpen(false); setFocusId(item.id); }} /></SheetContent></Sheet>
    <Dialog open={!!folderDialog} onOpenChange={open => { if (!open) { setFolderDialog(null); setFolderName(""); } }}><DialogContent className="admin-modal-content"><form onSubmit={event => { event.preventDefault(); saveFolder(); }}><DialogHeader><DialogTitle>{folderDialog?.mode === "rename" ? "Rename folder" : "Create folder"}</DialogTitle><DialogDescription>{folderDialog?.mode === "rename" ? "Choose a new name for this folder." : "Create a folder to organise knowledge items."}</DialogDescription></DialogHeader><Input autoFocus className="my-5" aria-label="Folder name" placeholder="Folder name" maxLength={80} value={folderName} onChange={event => setFolderName(event.target.value)} /><DialogFooter><Button type="button" variant="outline" onClick={() => { setFolderDialog(null); setFolderName(""); }}>Cancel</Button><Button type="submit">{folderDialog?.mode === "rename" ? "Save" : "Create folder"}</Button></DialogFooter></form></DialogContent></Dialog>
    <Dialog open={!!deletingFolderId} onOpenChange={open => !open && setDeletingFolderId(null)}><DialogContent className="admin-modal-content"><DialogHeader><DialogTitle>Delete folder?</DialogTitle><DialogDescription>The folder will be removed. Its knowledge items will return to All knowledge and will not be deleted.</DialogDescription></DialogHeader><DialogFooter><Button variant="outline" onClick={() => setDeletingFolderId(null)}>Cancel</Button><Button variant="destructive" onClick={() => deletingFolderId && deleteFolder(deletingFolderId)}>Delete folder</Button></DialogFooter></DialogContent></Dialog>
    <Dialog open={deleting.length > 0} onOpenChange={open => !open && !busy && setDeleting([])}><DialogContent className="admin-modal-content"><DialogHeader><DialogTitle>Delete knowledge?</DialogTitle><DialogDescription>{deleting.length} {deleting.length === 1 ? "item will" : "items will"} be permanently removed, including uploaded files. Your AI will stop using this content.</DialogDescription></DialogHeader><DialogFooter><Button variant="outline" disabled={busy} onClick={() => setDeleting([])}>Cancel</Button><Button variant="destructive" disabled={busy} onClick={() => void deleteItems()}>{busy ? "Deleting…" : "Delete"}</Button></DialogFooter></DialogContent></Dialog>
  </div>;
}

function TestPanel({ businessId, items, onOpenItem }: { businessId: string | null; items: KnowledgeItem[]; onOpenItem: (item: KnowledgeItem) => void }) {
  const [question, setQuestion] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [answer, setAnswer] = useState("");
  const [references, setReferences] = useState<{ id: string; name: string; text: string }[]>([]);
  const ask = async (text: string) => {
    if (!businessId || loading || !text.trim()) return;
    setQuestion(text); setSubmitted(text); setLoading(true); setError(""); setAnswer(""); setReferences([]);
    try {
      const result = await knowledgeRequest(businessId,"test",{ question: text });
      setAnswer(result.answer ?? ""); setReferences(result.sources ?? []);
      // Answered: clear the box for the next question. On an error it stays, so a retry is one click.
      setQuestion("");
    } catch (error) { setError((error as Error).message); }
    finally { setLoading(false); }
  };
  return <div className="flex h-full flex-col">
    <SheetHeader className="border-b pb-5 pr-6 text-left"><SheetTitle className="flex items-center gap-2 font-heading"><Sparkles className="h-5 w-5" />Test AI knowledge</SheetTitle><SheetDescription>Check what your AI knows about your business, like an employee taking a product knowledge test. Review its answers and sources.</SheetDescription></SheetHeader>
    <div className="scrollbar-hide min-h-0 flex-1 space-y-5 overflow-y-auto py-6" aria-live="polite">
      {!submitted && <div className="space-y-2"><p className="text-xs font-medium text-muted-foreground">Try a question</p>{["Do you know what services we offer?", "Explain our cancellation policy.", "What promotions are we currently running?"].map(text => <button key={text} onClick={() => void ask(text)} className="block w-full rounded-lg border p-3 text-left text-sm hover:bg-muted/40">{text}</button>)}</div>}
      {submitted && <p className="rounded-lg bg-muted/50 p-4 text-sm">{submitted}</p>}
      {loading && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-label="Generating answer" />}
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      {answer && <p className="whitespace-pre-wrap text-sm leading-6">{answer}</p>}
      {references.length > 0 && <><h3 className="text-sm font-medium">Sources</h3>{references.map(source => <button key={source.id} className="block w-full space-y-2 rounded-lg border p-4 text-left hover:bg-muted/30" onClick={() => { const item = items.find(item => item.id === source.id); if (item) onOpenItem(item); }}><p className="text-sm font-medium">{source.name}</p><p className="line-clamp-3 text-xs leading-5 text-muted-foreground">{source.text}</p></button>)}</>}
    </div>
    <form className="flex gap-2 border-t pt-4" onSubmit={e => { e.preventDefault(); void ask(question); }}><Input aria-label="Test question" maxLength={4000} value={question} onChange={e => setQuestion(e.target.value)} placeholder="Ask about your business…" /><Button type="submit" disabled={!question.trim() || loading || !businessId} aria-label="Send question"><ArrowUp className="h-4 w-4" /></Button></form>
  </div>;
}
